import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BDK, waUrl, SITE_URL, NEXO_ENABLED } from "../config/bdk";
import useReveal from "../hooks/useReveal";
import Seo from "../components/Seo";
import PortfolioCard from "../components/landing/PortfolioCard";
import { Footer } from "../components/landing/FinalCTAFooter";
import WhatsAppFloat from "../components/landing/WhatsAppFloat";
import WhatsAppLeadModal from "../components/landing/WhatsAppLeadModal";

const MiniHeader = () => (
  <header
    data-testid="portfolio-paginas-header"
    className="relative"
    style={{ background: "var(--ink)", borderBottom: "1px solid #2a2b34" }}
  >
    <div className="container-bdk flex items-center justify-between gap-4 h-[72px]">
      <Link
        to="/"
        data-testid="portfolio-paginas-logo"
        className="block w-[170px] md:w-[190px] flex-shrink-0"
      >
        <img
          src={BDK.logos.redWhite}
          alt="BDKSISTEMAS"
          className="w-full h-auto object-contain"
        />
      </Link>
      <Link
        to="/"
        data-testid="portfolio-paginas-back-home"
        className="inline-flex items-center gap-2 text-white/80 hover:text-[#d90020] text-[12px] sm:text-[13px] font-bold uppercase tracking-wide transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="hidden sm:inline">¿Necesitas una página web?</span>{" "}
        Volver al inicio
      </Link>
    </div>
  </header>
);

const PortfolioPaginasHero = () => (
  <section
    data-testid="section-portfolio-paginas-hero"
    className="bdk-section"
    style={{ background: "var(--paper)" }}
  >
    <div className="container-bdk max-w-[820px] mx-auto text-center">
      <p className="kicker">Portafolio de páginas web</p>
      <h1 className="font-display text-[38px] sm:text-[48px] md:text-[58px]">
        Todos los sitios que hemos
        <br />
        diseñado hasta hoy.
      </h1>
      <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
        Sitios reales diseñados por BDKSISTEMAS. Tu página se adapta al giro,
        colores y estilo de tu negocio.
      </p>
    </div>
  </section>
);

const PortfolioPaginasGrid = () => {
  useReveal();
  return (
    <section
      data-testid="section-portfolio-paginas-grid"
      className="bdk-section"
      style={{ background: "var(--paper)", paddingTop: 0 }}
    >
      <div className="container-bdk">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BDK.portfolio.map((p, i) => (
            <PortfolioCard
              key={p.name}
              p={p}
              i={i}
              testIdPrefix="portfolio-paginas-card"
            />
          ))}
        </div>

        <div
          className="mt-12 rounded-[14px] bg-white p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bdk-reveal bdk-reveal--up"
          style={{ border: "1px solid var(--line)" }}
        >
          <div>
            <h3 className="font-display text-[26px] md:text-[28px] uppercase leading-none">
              ¿Quieres una página así para tu negocio?
            </h3>
            <p className="text-[#666873] text-[13px] mt-2">
              La personalizamos con tu logo, colores, promociones y estilo.
            </p>
          </div>
          <a
            href={waUrl(
              "Hola, vi el portafolio completo de BDKSISTEMAS y quiero información para una página como esas para mi negocio."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="portfolio-paginas-inline-cta"
            onClick={() => window.fbq?.("trackCustom", "ClickPortafolio")}
            className="bdk-btn bdk-btn--primary flex-shrink-0"
          >
            Quiero una página así
          </a>
        </div>
      </div>
    </section>
  );
};

const portfolioJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Portafolio de páginas web",
        item: `${SITE_URL}/portafolio-paginas`,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: BDK.portfolio.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.name,
      url: p.url,
    })),
  },
];

const PortfolioPaginas = () => {
  const [waHref, setWaHref] = React.useState(null);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest('a[href*="wa.me"]');
      if (link && !link.hasAttribute("data-no-lead-modal")) {
        e.preventDefault();
        setWaHref(link.href);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div
      data-testid="portfolio-paginas-page"
      className="min-h-screen bg-white text-[#101010]"
    >
      <Seo
        title="Portafolio de Páginas Web | BDKSISTEMAS"
        description="Portafolio completo de páginas web y landing pages diseñadas por BDKSISTEMAS para gimnasios, salones, restaurantes y negocios de servicios."
        path="/portafolio-paginas"
        jsonLd={portfolioJsonLd}
      />
      <MiniHeader />
      <main>
        <PortfolioPaginasHero />
        <PortfolioPaginasGrid />
      </main>
      <Footer />
      {!NEXO_ENABLED && <WhatsAppFloat />}
      <WhatsAppLeadModal
        open={!!waHref}
        href={waHref}
        onClose={() => setWaHref(null)}
      />
    </div>
  );
};

export default PortfolioPaginas;
